import { useEffect, useRef, useState } from "react";
import { useInView } from "./Reveal";

const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const fine = () => typeof window !== "undefined" && window.matchMedia?.("(pointer: fine)").matches && window.innerWidth >= 1024;

/* ------------------------------------------------------------------ */
/* Shared rAF scroll bus – one listener, many subscribers.             */
/* ------------------------------------------------------------------ */
const subs = new Set();
let ticking = false;
const flush = () => { ticking = false; subs.forEach((fn) => fn()); };
const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(flush); } };
const subscribe = (fn) => {
  if (subs.size === 0) {
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
  }
  subs.add(fn);
  fn();
  return () => {
    subs.delete(fn);
    if (subs.size === 0) {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    }
  };
};
export const onScrollFrame = subscribe;

/** Calls fn(progress 0..1, rect) while the element travels through the viewport. */
export const useScrollProgress = (ref, fn, { start = 1, end = 0 } = {}) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return subscribe(() => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = r.height + vh * (start - end);
      const p = total <= 0 ? 1 : Math.min(1, Math.max(0, (vh * start - r.top) / total));
      fn(p, r);
    });
  }, [ref, fn, start, end]);
};

/** Soft parallax: translates the media inside a section as it scrolls by. Desktop + motion-ok only. */
export const useParallax = (ref, { speed = 0.18, scale = 1 } = {}) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || reduced() || window.innerWidth < 768) return;
    const parent = el.parentElement;
    return subscribe(() => {
      const r = parent.getBoundingClientRect();
      const vh = window.innerHeight;
      if (r.bottom < 0 || r.top > vh) return;
      const centre = r.top + r.height / 2 - vh / 2;
      const y = -centre * speed;
      el.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0) scale(${scale})`;
    });
  }, [ref, speed, scale]);
};

/* ------------------------------------------------------------------ */
/* SplitWords – masked word-by-word headline reveal                    */
/* ------------------------------------------------------------------ */
export const SplitWords = ({ text = "", as: Tag = "span", className = "", delay = 0, step = 70, on = true, ...rest }) => {
  const words = String(text).split(/\s+/).filter(Boolean);
  return (
    <Tag className={`kinetic ${on ? "is-in" : ""} ${className}`} {...rest}>
      {words.map((w, i) => (
        <span key={`${w}-${i}`} className="kw">
          <span className="kw-i" style={{ "--d": `${delay + i * step}ms` }}>{w}</span>
          {i < words.length - 1 ? " " : ""}
        </span>
      ))}
    </Tag>
  );
};

/** SplitWords that triggers when scrolled into view. */
export const SplitWordsInView = ({ as: Tag = "span", className = "", ...rest }) => {
  const [ref, on] = useInView({ threshold: 0.35 });
  return (
    <Tag ref={ref} className={className}>
      <SplitWords on={on} {...rest} />
    </Tag>
  );
};

export const prefersReducedMotion = reduced;
export const isFinePointer = fine;
